import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { HelpCircle, ShieldCheck, KeyRound, Lock } from 'lucide-react';
interface SlideObjectionsProps {
  onContinue: () => void;
}
const DEPENDENCIES = ['Agencies', 'Internal teams', 'Technology', 'Production partners', 'Data providers', 'Compliance systems'];
const ACCESS = ['All campaign content', 'All Snaks & approved assets', 'All storyboards', 'All local versions', 'All campaign data', 'All performance reporting', 'Standardized workflows', 'The HQ Control Tower'];
export function SlideObjections({
  onContinue
}: SlideObjectionsProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [300, 900, 1600, 2400].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto" data-id="element-1001" data-mp="139a40s09cvsi">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-3 shrink-0" data-id="element-1002" data-mp="1g3iakmi7oz1a">
        <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-1003" data-mp="16dfh6l76hj2f">
          But… the objection
        </p>
        <h2 className="text-3xl md:text-5xl font-serif text-white" data-id="element-1004" data-mp="11fneqx8wqejz">
          "Doesn't this make us dependent on Snakker?"
        </h2>
        <p className="text-lg text-white/60" data-id="element-1005" data-mp="1z1kzz2eo8xzm">
          Dependency becomes managed capability — with more control, not less.
        </p>
      </motion.div>

      <div className="flex-1 min-h-0 flex flex-col justify-center max-w-6xl mx-auto w-full space-y-5 mt-6" data-id="element-1006" data-mp="1p3f16to5w7yz">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-id="element-1007" data-mp="1l1js32zzoqan">
          {/* Every model depends */}
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: stage >= 1 ? 1 : 0,
          y: stage >= 1 ? 0 : 14
        }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-6" data-id="element-1008" data-mp="dq2wyhohpnca">
            <div className="w-10 h-10 rounded-xl bg-navy-700 text-white/60 flex items-center justify-center mb-4" data-id="element-1009" data-mp="g36ejek59oea">
              <HelpCircle className="w-5 h-5" data-id="element-1010" data-mp="18i920equpyo" />
            </div>
            <h3 className="text-white font-semibold mb-1" data-id="element-1011" data-mp="647doi5mex1u">
              Every model depends on capabilities
            </h3>
            <p className="text-white/50 text-sm mb-4 leading-relaxed" data-id="element-1012" data-mp="3oi6vpdhujcn8">
              The question was never whether there is a dependency — but whether
              it is transparent, governed, measurable, replaceable and
              cost-controlled.
            </p>
            <div className="flex flex-wrap gap-2" data-id="element-1013" data-mp="3jlgdajikk4yn">
              {DEPENDENCIES.map(item => <span key={item} className="px-3 py-1.5 rounded-full border border-navy-600 text-white/60 text-xs font-medium" data-id="element-1014" data-mp="35low69wra8gf">
                  {item}
                </span>)}
            </div>
          </motion.div>

          {/* The two real worries */}
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: stage >= 2 ? 1 : 0,
          y: stage >= 2 ? 0 : 14
        }} className="space-y-4" data-id="element-1015" data-mp="2m0ooe1ruxstx">
            <div className="bg-navy-700/40 border border-navy-600 rounded-2xl p-5" data-id="element-1016" data-mp="39vrf6fa826d1">
              <div className="flex items-center gap-2.5 mb-2" data-id="element-1017" data-mp="34yhs36ab26sd">
                <Lock className="w-4 h-4 text-rose-400 shrink-0" data-id="element-1018" data-mp="34vvhsyw15d6g" />
                <p className="text-rose-400 font-semibold text-xs uppercase tracking-widest" data-id="element-1019" data-mp="3iol8hgmi769w">
                  Worry 1 — Lock-in
                </p>
              </div>
              <p className="text-white font-semibold text-sm mb-1.5" data-id="element-1020" data-mp="3smhig53pfv6f">
                "We won't be able to move."
              </p>
              <p className="text-white/70 text-sm leading-relaxed" data-id="element-1021" data-mp="3npqyh6os4ffq">
                Everything is exit-ready by design: transferable assets,
                documented campaigns, standardized workflows and full data
                export — so the capability can be moved in-house or to another
                partner at any renewal point.
              </p>
            </div>

            <div className="bg-navy-700/40 border border-navy-600 rounded-2xl p-5" data-id="element-1022" data-mp="pb9o68bp0x4f">
              <div className="flex items-center gap-2.5 mb-2" data-id="element-1023" data-mp="3os7yvc6w70ip">
                <ShieldCheck className="w-4 h-4 text-sky-400 shrink-0" data-id="element-1024" data-mp="3otstf1t7ijgk" />
                <p className="text-sky-400 font-semibold text-xs uppercase tracking-widest" data-id="element-1025" data-mp="3stbdxwh48siv">
                  Worry 2 — Price escalation
                </p>
              </div>
              <p className="text-white font-semibold text-sm mb-1.5" data-id="element-1026" data-mp="6mv09two9z7u">
                "The vendor can raise the price later."
              </p>
              <p className="text-white/70 text-sm leading-relaxed" data-id="element-1027" data-mp="1d49ndxd9b9gp">
                Handled by an{' '}
                <span className="text-teal-400 font-semibold" data-id="element-1028" data-mp="1i2t9pyvrin9l">
                  enterprise fixed-cost agreement
                </span>{' '}
                — a fixed annual price for an agreed portfolio scope, with unit
                economics and service levels locked for the term. No per-request
                pricing, no surprise escalation.
              </p>
            </div>
          </motion.div>

          {/* What LEO gets */}
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          y: stage >= 3 ? 0 : 14
        }} className="bg-teal-400/10 border border-teal-400/30 rounded-2xl p-6" data-id="element-1029" data-mp="2jtksztij5fdh">
            <div className="w-10 h-10 rounded-xl bg-teal-400/20 text-teal-400 flex items-center justify-center mb-4" data-id="element-1030" data-mp="24wtr9dldvrkp">
              <KeyRound className="w-5 h-5" data-id="element-1031" data-mp="80wb4biyozzu" />
            </div>
            <h3 className="text-white font-semibold mb-1" data-id="element-1032" data-mp="34ywqar9e3pp">
              LEO owns every output
            </h3>
            <p className="text-white/50 text-sm mb-4 leading-relaxed" data-id="element-1033" data-mp="1xjcgv5vvdmcr">
              Full access, transferable assets, visible costs, agreed service
              levels.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-1034" data-mp="1nmzku1cuqweu">
              {ACCESS.map(item => <div key={item} className="flex items-start gap-2 text-white/85 text-xs bg-navy-900/50 border border-navy-700 rounded-lg px-2.5 py-2 leading-snug" data-id="element-1035" data-mp="1slt1g2nfr3j8">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0 mt-1" data-id="element-1036" data-mp="2l8x96fdwxvyp" />
                  {item}
                </div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}